/**
 * FormModal Component
 * 
 * Enhanced Modal wrapper that automatically handles:
 * - Unsaved changes detection (via react-hook-form isDirty)
 * - Confirmation dialog before discarding changes
 * - Auto-save drafts to localStorage
 * - Draft recovery on modal open
 * 
 * Usage:
 * <FormModal
 *   open={modalOpen}
 *   onOpenChange={handleModalOpenChange}
 *   title="Novo Produto"
 *   description="Preencha os dados"
 *   formId="product"
 *   entityId={product?.id}
 *   isDirty={formState.isDirty}
 *   formValues={watch()}
 *   onLoadDraft={(draft) => reset(draft)}
 *   onSave={handleSubmit(onSubmit)}
 *   loading={saving}
 * >
 *   <form>...</form>
 * </FormModal>
 */
import React, { useCallback, useState, useEffect } from 'react';
import { Modal } from './modal';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { Badge } from './badge';
import { Button } from './button';
import { useFormDraft } from '../../hooks/useFormDraft';
import { RotateCcw, Clock } from 'lucide-react';

export function FormModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  // Form state
  isDirty = false,
  formValues = null,
  onLoadDraft = null,
  // Draft settings
  formId = null,
  entityId = null,
  enableDraft = true,
  // Save/loading
  onSave,
  loading = false,
  confirmText = 'Salvar',
  cancelText = 'Cancelar',
  // Modal settings
  size = 'default',
  showDirtyIndicator = true,
  footer,
  ...props
}) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDraftBanner, setShowDraftBanner] = useState(false);

  // Draft management
  const {
    hasDraft,
    loadDraft,
    saveDraft,
    clearDraft,
    formatDraftTime,
  } = useFormDraft(formId || 'form', {
    entityId,
    enabled: enableDraft && !!formId,
    debounceMs: 1500,
  });

  // Check for draft when modal opens
  useEffect(() => {
    if (open && hasDraft && !entityId) {
      // Only show banner for new items, not edits
      setShowDraftBanner(true);
    } else {
      setShowDraftBanner(false);
    }
  }, [open, hasDraft, entityId]);

  // Auto-save draft when form changes
  useEffect(() => {
    if (open && isDirty && formValues && enableDraft && formId) {
      saveDraft(formValues);
    }
  }, [open, isDirty, formValues, saveDraft, enableDraft, formId]);

  // Handle draft recovery
  const handleRecoverDraft = useCallback(() => {
    const draft = loadDraft();
    if (draft && onLoadDraft) {
      onLoadDraft(draft);
      setShowDraftBanner(false);
    }
  }, [loadDraft, onLoadDraft]);

  // Handle discard draft
  const handleDiscardDraft = useCallback(() => {
    clearDraft();
    setShowDraftBanner(false);
  }, [clearDraft]);

  // Handle close attempt
  const handleOpenChange = useCallback((newOpen) => {
    if (!newOpen && isDirty) {
      // User trying to close with unsaved changes
      setShowConfirmDialog(true);
      return; // Don't close yet
    }
    if (!newOpen) {
      // Clear draft banner state on close
      setShowDraftBanner(false);
    }
    onOpenChange(newOpen);
  }, [isDirty, onOpenChange]);

  // Confirm discard and close
  const handleConfirmDiscard = useCallback(() => {
    setShowConfirmDialog(false);
    setShowDraftBanner(false);
    // Don't clear draft on discard - user might want to recover later
    onOpenChange(false);
  }, [onOpenChange]);

  // Cancel close attempt
  const handleCancelClose = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  // Handle successful save
  const handleSave = useCallback(() => {
    // Clear draft on save
    if (enableDraft && formId) {
      clearDraft();
    }
    onSave?.();
  }, [onSave, clearDraft, enableDraft, formId]);

  // Enhanced title with dirty indicator
  const enhancedTitle = (
    <span className="flex items-center gap-2">
      {title}
      {showDirtyIndicator && isDirty && (
        <Badge variant="outline" className="text-yellow-600 border-yellow-500 bg-yellow-500/10 text-xs">
          Não salvo
        </Badge>
      )}
    </span>
  );

  // Draft recovery banner
  const DraftBanner = showDraftBanner ? (
    <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-blue-500" />
          <span className="text-blue-700 dark:text-blue-300">
            Rascunho encontrado ({formatDraftTime()})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRecoverDraft}
            className="h-7 text-xs"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Recuperar
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleDiscardDraft}
            className="h-7 text-xs text-muted-foreground"
          >
            Descartar
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <Modal
        open={open}
        onOpenChange={handleOpenChange}
        title={enhancedTitle}
        description={description}
        size={size}
        onConfirm={handleSave}
        onCancel={() => handleOpenChange(false)}
        confirmText={loading ? 'Salvando...' : confirmText}
        cancelText={cancelText}
        loading={loading}
        footer={footer}
        {...props}
      >
        {DraftBanner}
        {children}
      </Modal>

      <UnsavedChangesDialog
        open={showConfirmDialog}
        onConfirm={handleConfirmDiscard}
        onCancel={handleCancelClose}
      />
    </>
  );
}

export default FormModal;
